// Global keyboard-shortcut thunks extracted from
// `src/modules/Services/ServicesThunks.js`.

import _ from "lodash";
import { isTypingInInput } from "Utils";
import { recordEvent } from "lib/eventTracker";
import { getUserInfo, isLibraryRoute } from "Login/modules/LoginModule";
import { getAdminPageRoute } from "AdminSettings/utils/AdminSettingsUtils";
import { setJsonItem } from "services/localStorage";
import { updateParentEntity } from "ToddleLibrary/modules/ToddleLibraryModule";
import {
  setActiveFeatureWithKeyboardShortcuts,
  toggleVoiceSearch,
  toggleGlobalSearchBarModal,
  toggleSearchAndVoiceModule,
} from "modules/Services/ServicesActions";
import {
  _getIsGlobalSearchJumpToRevampEnabled,
  _getIsGlobalSearchJumpToV2Enabled,
  _shouldTriggerGlobalSearch,
} from "modules/Services/ServicesUtils";
import { goToRelativeRoute } from "modules/Services/ServicesThunks";

const trackFeatureActivationEvents = ({
  featureName,
  shouldActivateFeature,
  e,
}) => {
  switch (featureName) {
    case "JUMP_TO_FEATURE":
      {
        recordEvent({
          eventName: "JUMP_TO",
          eventData: {
            event_type: shouldActivateFeature ? "OPEN" : "CLOSE",
            triggerPoint: "KEYBOARD",
            shortcutKey: {
              key: e?.code,
              isMeta: e?.ctrlKey || e?.metaKey || e?.shiftKey || e?.altKey,
            },
          },
        });
      }
      break;
    default:
      return;
  }
};

/**
 * @deprecated
 */

const handleFeatureActivation = (featureName, e) => {
  return (dispatch, getState) => {
    /**
     * If user is typing in a input or text area field, we don't need to process event further
     */
    if (isTypingInInput()) {
      return;
    }

    const {
      featuresWithKeyboardShortcuts,
      activeFeatureWithKeyboardShortcuts,
      globalSearchBar: { showSearchBar },
    } = getState().app_services;

    /**
     * Check if feature is supported and avoid unnecessary processing
     */
    if (!_.includes(featuresWithKeyboardShortcuts, featureName)) {
      return;
    }

    const currentActiveFeature = activeFeatureWithKeyboardShortcuts;
    const shouldActivateFeature = currentActiveFeature !== featureName;

    trackFeatureActivationEvents({
      featureName,
      shouldActivateFeature,
      e,
    });

    dispatch(
      setActiveFeatureWithKeyboardShortcuts(
        shouldActivateFeature ? featureName : null
      )
    );
    if (shouldActivateFeature && showSearchBar) {
      dispatch(toggleGlobalSearchBarModal());
    }
  };
};

export const checkAndClearActiveFeatureWithKeyboardShortcuts = feature => {
  return async (dispatch, getState) => {
    const { activeFeatureWithKeyboardShortcuts } = getState().app_services;

    if (activeFeatureWithKeyboardShortcuts !== feature) return;

    dispatch(setActiveFeatureWithKeyboardShortcuts(null));
  };
};

export const handleGlobalKeyboardEvents = (e, routeCourseId) => {
  return async (dispatch, getState) => {
    const state = getState();

    const userInfo = getUserInfo();
    const { userCurriculumPrograms } = userInfo;

    const isGlobalSearchModalOpen = _.get(
      state,
      "app_services.globalSearchBar.showSearchBar",
      false
    );

    const isJumpToFeatureOpen =
      _.get(state, "app_services.activeFeatureWithKeyboardShortcuts") ===
      "JUMP_TO_FEATURE";

    const key = _.toLower(e.key);

    const isGlobalSearchJumpToRevampEnabled = _getIsGlobalSearchJumpToRevampEnabled();
    const isJumpToV2Enabled = _getIsGlobalSearchJumpToV2Enabled();

    const shouldDisableJumpToActivation =
      isGlobalSearchJumpToRevampEnabled && !isJumpToV2Enabled;

    const activeTab = _.get(state, "login.activeTab");
    const isPAMTab = activeTab === "platformAccessManager";

    if (
      !shouldDisableJumpToActivation &&
      key === "j" &&
      !isGlobalSearchModalOpen &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.shiftKey &&
      !e.altKey &&
      activeTab !== "community" &&
      !isLibraryRoute(routeCourseId) &&
      !isPAMTab &&
      !_.isEmpty(userCurriculumPrograms)
    ) {
      //jump to feature
      dispatch(handleFeatureActivation("JUMP_TO_FEATURE", e));
      return;
    }

    if (e.key === "Escape" && isJumpToFeatureOpen) {
      dispatch(setActiveFeatureWithKeyboardShortcuts(null));
    }

    // activate these shortcuts only when global search or jumpto modal is not open
    if (!isGlobalSearchModalOpen && !isJumpToFeatureOpen && !isPAMTab) {
      if (e.shiftKey && e.code === "KeyO") {
        dispatch(handleFeatureActivation("CALENDAR"));
      }

      if (
        e.code === "KeyN" &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey &&
        !e.altKey
      ) {
        dispatch(handleFeatureActivation("NOTIFICATION"));
        dispatch(handleFeatureActivation("GLOBAL_MENU_NOTIFICATION"));
      }

      if (
        e.code === "KeyM" &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey &&
        !e.altKey
      ) {
        dispatch(handleFeatureActivation("GLOBAL_MENU_MESSAGING"));
      }

      if (
        e.code === "KeyH" &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey &&
        !e.altKey
      ) {
        dispatch(_handleHomePageNavigation());
      }

      if (
        e.code === "KeyX" &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey &&
        !e.altKey
      ) {
        dispatch(handleFeatureActivation("PLATFORM_SWITCHER"));
      }

      if (
        e.code === "KeyK" &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey &&
        !e.altKey
      ) {
        dispatch(handleFeatureActivation("SHORTCUT_MANAGER"));
      }
    }

    const openModal = _shouldTriggerGlobalSearch(e);

    if (openModal && !isJumpToFeatureOpen) {
      //handle global search
      dispatch(handleGlobalSearch(e, routeCourseId));
      dispatch(setActiveFeatureWithKeyboardShortcuts("GLOBAL_SEARCH"));
    }
  };
};

//TODO: Integrate this with new flow of handling global keyboard events
//this to handle global search state

export const handleGlobalSearch = (e, routeCourseId) => {
  return async (dispatch, getState) => {
    //this is basic condition to open and close global search modal
    const openModal = _shouldTriggerGlobalSearch(e);

    const { showSearchBar } = getState().app_services.globalSearchBar;
    const activeTab = getState().login.activeTab;
    const userInfo = getUserInfo();
    const { userCurriculumPrograms } = userInfo;
    const pamAndCommunityTab = ["community", "platformAccessManager"];

    if (
      _.includes(pamAndCommunityTab, activeTab) ||
      isLibraryRoute(routeCourseId) ||
      _.isEmpty(userCurriculumPrograms)
    ) {
      return;
    }

    if (openModal && e.shiftKey && showSearchBar) {
      dispatch(toggleVoiceSearch());
    } else if (openModal && e.shiftKey) {
      dispatch(toggleSearchAndVoiceModule());
    } else if (openModal) {
      if (!showSearchBar) {
        // if modal is to be opened - fire a tracking event
        recordEvent({
          eventName: "GLOBAL_SEARCH",
          eventData: {
            event_type: "OPEN",
            triggerPoint: "KEYBOARD",
            shortcutKey: {
              key: e.code,
              isMeta: e.ctrlKey || e.metaKey || e.shiftKey || e.altKey,
            },
          },
        });
      }
      dispatch(toggleGlobalSearchBarModal());
    }

    //handle removal of searchbar
    if (e.key === "Escape" && showSearchBar) {
      dispatch(toggleGlobalSearchBarModal());
    }
  };
};

const _handleHomePageNavigation = () => {
  return (dispatch, getState) => {
    const featuresWithKeyboardShortcuts = _.get(
      getState(),
      "app_services.featuresWithKeyboardShortcuts",
      []
    );

    if (isTypingInInput()) {
      return;
    }

    if (!_.includes(featuresWithKeyboardShortcuts, "GO_TO_HOME")) {
      return;
    }

    const currentCurriculumProgramId = _.get(
      getState(),
      "platform.currentCurriculumProgram.id",
      null
    );

    const activeTab = _.get(getState(), "login.activeTab");
    switch (activeTab) {
      case "teacher": {
        dispatch(
          _goToPlatformHomeRoute({
            curriculumProgramId: _.get(
              getState(),
              "platform.currentCurriculumProgram.id",
              null
            ),
            navigateType: "replace",
          })
        );
        break;
      }

      case "MY_HOME": {
        dispatch(
          goToRelativeRoute({
            route: "/platform/my-home",
            type: "replace",
          })
        );
        break;
      }

      case "admin": {
        dispatch(
          goToRelativeRoute({
            route: getAdminPageRoute(),
            type: "replace",
          })
        );
        break;
      }

      case "accountSettingsTab": {
        dispatch(
          goToRelativeRoute({
            route: `/platform/accountSettings/account-information`,
            type: "replace",
          })
        );
        break;
      }

      case "schoolSettingsTab": {
        dispatch(
          goToRelativeRoute({
            route: `/platform/school/${currentCurriculumProgramId}/school-information`,
            type: "replace",
          })
        );
        break;
      }

      case "TODDLE_LIBRARY": {
        dispatch(updateParentEntity("TODDLE_LIBRARY"));

        dispatch(
          goToRelativeRoute({
            route: "/toddle-library",
            type: "replace",
          })
        );
        break;
      }

      default: {
        console.error("The shortcut is not implemented for this tab");
      }
    }

    recordEvent({
      eventName: "GLOBAL_SHORTCUTS",
      eventData: {
        event_type: "TODDLE_HOME",
        triggerPoint: "KEYBOARD",
        portal: _.toUpper(activeTab),
      },
    });
  };
};

/**
 * New function is created to avoid circular dependency with
 * Navigation module
 */
const _goToPlatformHomeRoute = ({
  navigateType = "push",
  curriculumProgramId,
} = {}) => {
  return (dispatch, getState) => {
    const state = getState();

    const organizationCurriculumPrograms = _.get(
      getState(),
      "platform.organizationCurriculumPrograms",
      []
    );

    const curriculumProgram = _.find(organizationCurriculumPrograms, {
      id: curriculumProgramId,
    });

    const finalCurriculumProgram = _.pick(curriculumProgram, [
      "id",
      "type",
      "label",
    ]);
    setJsonItem({
      key: "currentCurriculumProgram",
      value: finalCurriculumProgram,
    });

    dispatch(
      goToRelativeRoute({
        route: `/platform/${curriculumProgramId}`,
        type: navigateType,
      })
    );
  };
};
